// Public product data comes exclusively from Supabase via
// `src/lib/public.functions.ts`. This module now only exposes the
// shared `Product` type, category/animal labels, and price helpers.

export type Animal = "manzo" | "vitello" | "maiale" | "pollo" | "tacchino" | "agnello" | "selvaggina" | "vari";
export type Category =
  | "selezione-macellaio"
  | "piatti-pronti"
  | "preparati"
  | "insaccati"
  | "abatina"
  | "preconfezionati";

export interface Product {
  slug: string;
  name: string;
  category: Category;
  animal: Animal;
  price: number;
  unit: "kg" | "pz";
  shortDescription: string;
  longDescription: string[];
  specs: { label: string; value: string }[];
  image: string;
  gallery?: string[];
  featured?: boolean;
  bookable?: boolean;
  onOffer?: boolean;
  offerPrice?: number;
}

export const CATEGORY_LABELS: Record<Category, string> = {
  "selezione-macellaio": "Selezione del Macellaio",
  "piatti-pronti": "Piatti Pronti",
  preparati: "Preparati",
  insaccati: "Insaccati & Salumi",
  abatina: "L'Abatina",
  preconfezionati: "Preconfezionati",
};

export const ANIMAL_LABELS: Record<Animal, string> = {
  manzo: "Manzo",
  vitello: "Vitello",
  maiale: "Maiale",
  pollo: "Pollo",
  tacchino: "Tacchino",
  agnello: "Agnello",
  selvaggina: "Selvaggina",
  vari: "Misti",
};

function hasPrice(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n) && n > 0;
}

export function effectivePrice(p: Product): number {
  if (p.onOffer && hasPrice(p.offerPrice)) return p.offerPrice as number;
  return hasPrice(p.price) ? p.price : 0;
}

function fmt(n: number) {
  return `€ ${n.toFixed(2).replace(".", ",")}`;
}

/**
 * Prezzo visualizzato. Se il prezzo non è ancora stato inserito
 * (0 / null / mancante), mostra "Prezzo su richiesta" invece di € 0,00.
 */
export function formatPrice(p: Product) {
  const base = p.price;
  const offer = p.offerPrice;
  const hasBase = hasPrice(base);
  const hasOffer = p.onOffer && hasPrice(offer);

  if (!hasBase && !hasOffer) {
    return (
      <span className="font-italic-serif italic text-brand-warm-gray whitespace-nowrap">
        Prezzo su richiesta
      </span>
    );
  }

  if (hasOffer && hasBase && (offer as number) < base) {
    return (
      <span className="inline-flex items-baseline gap-2">
        <span className="text-slate-400 line-through text-sm">{fmt(base)}</span>
        <span className="text-brand-red font-semibold">{fmt(offer as number)}/{p.unit}</span>
      </span>
    );
  }

  const shown = hasOffer ? (offer as number) : base;
  return <>{fmt(shown)}/{p.unit}</>;
}
